import * as React from 'react';

import { useLocation } from 'js/lib/useRouter';

import { Link, Typography2 } from '@coursera/cds-core';

import { TrackedA } from 'bundles/page/components/TrackedLink2';
import RatingsBreakdown from 'bundles/unified-description-page-common/components/modules/reviews/RatingsBreakdown';
import ReviewCard from 'bundles/unified-description-page-common/components/modules/reviews/ReviewCard';
import ReviewsModule, {
  NUM_REVIEWS_TO_DISPLAY,
} from 'bundles/unified-description-page-common/components/modules/reviews/ReviewsModule';
import { usePageInfo } from 'bundles/unified-description-page-common/contexts/PageInfoContext';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import type { EnterpriseCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseCoursePageQuery';

import _t from 'i18n!nls/xdp';

type Props = {
  pageData: ConsumerCoursePageFieldsFragment | EnterpriseCoursePageFieldsFragment;
  config?: {
    disableCta?: boolean;
  };
};

const ReviewsModuleRenderer = ({ pageData, config }: Props) => {
  const { navigationIdMap, subfolderLocaleCode } = usePageInfo();
  const { pathname: path } = useLocation();

  let reviewsPathname = path?.endsWith('/') ? path?.slice(0, -1) : path;

  if (subfolderLocaleCode && reviewsPathname.includes(`/${subfolderLocaleCode}`)) {
    reviewsPathname = reviewsPathname.replace(`/${subfolderLocaleCode}`, '');
  }

  return (
    <ReviewsModule
      anchorId={navigationIdMap?.REVIEWS}
      title={_t('Learner reviews')}
      ratingsInfo={pageData.ratings}
      ratingsBreakdown={
        <RatingsBreakdown
          averageFiveStarRating={pageData?.ratings?.averageFiveStarRating}
          ratingCountsByStar={pageData?.ratingCountsByStar}
          ratingCount={pageData?.ratings?.ratingCount}
        />
      }
      reviews={pageData?.topReviews?.slice(0, NUM_REVIEWS_TO_DISPLAY).map((review, index) => {
        return <ReviewCard ariaHidden={false} review={review} key={review.id} trackingIndex={index} />;
      })}
      cta={
        !config?.disableCta && (
          <Link component={TrackedA} trackingName="reviews_module_view_more_cta" href={`${reviewsPathname}/reviews`}>
            <Typography2 component="span">{_t('View more reviews')}</Typography2>
          </Link>
        )
      }
    />
  );
};

export default ReviewsModuleRenderer;
